import { GooglePayProps } from '@adyen/adyen-web/dist/types/components/GooglePay/types';
import { PaymentMethodsResponse } from '@adyen/adyen-web/dist/types/core/ProcessResponse/PaymentMethodsResponse/types';
import classNames from 'classnames';
import _get from 'lodash.get';
import { useCallback } from 'react';

import { PaymentMessage } from '@zooplus/checkout-state';

import { GOOGLE_PAY_BUTTON_ID, GOOGLE_PAY_CONTAINER_ID } from '@config/consts';
import useGooglePay from '@hooks/useGooglePay';
import { useTracking } from '@hooks/useTracking';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { captureException } from '@services/sentryService';
import { AdyenEnvironment } from '@types-internal/AdyenTypes';
import { Nullable } from '@types-internal/index';

import styles from './GooglePayButton.module.scss';
import { GooglePayErrorType, GooglePaySubmitFn } from './GooglePayButton.static';

import '@adyen/adyen-web/dist/adyen.css';

function getGooglePayErrorType(errorType: Nullable<string>): GooglePayErrorType {
  if (errorType === 'CANCEL') {
    return 'payment';
  }
  return 'initialization';
}

export interface GooglePayButtonProps {
  countryCode: string;
  // The "amount" in minor units
  amount: number;
  paymentMethodsResponse: PaymentMethodsResponse;
  onSubmit: GooglePaySubmitFn;
  addPaymentFrontendError?: (paymentFrontendError?: PaymentMessage) => void;
  buttonColor?: GooglePayProps['buttonColor'];
  buttonType?: GooglePayProps['buttonType'];
  siteId?: number;
  sid?: string;
  currency: string;
  customerId?: string;
  customerType?: string;
  adyenEnvironment: AdyenEnvironment;
}

const GooglePayButton: React.FC<GooglePayButtonProps> = ({
  adyenEnvironment,
  countryCode,
  amount,
  paymentMethodsResponse,
  onSubmit,
  addPaymentFrontendError,
  buttonColor = 'black',
  buttonType = 'pay',
  currency,
  siteId,
  sid,
  customerId,
  customerType,
}) => {
  const { t } = useTranslation();
  const { locale } = useAppContext();
  const { setError } = useErrorContext();
  const track = useTracking({
    currency,
    customerId,
    sid,
    siteId,
    guest: customerType === 'GUEST_CUSTOMER',
  });

  const {
    config: {
      adyen: { clientKey },
    },
  } = useConfig();

  const onFatalError = useCallback(
    (error: unknown) => {
      const errorType: Nullable<string> = _get(error, 'name', null);
      if (errorType !== 'CANCEL') {
        setError({
          message: t(`checkout.payment.googlepay.error.${getGooglePayErrorType(errorType)}`),
        });
        captureException({ error });
        addPaymentFrontendError?.({
          errorCode: 'GOOGLE_PAY_PAYMENT_UNSUCCESSFUL_ERROR',
        });
      } else {
        track?.('Click_CloseModal_GP');
      }
    },
    [addPaymentFrontendError],
  );

  useGooglePay({
    adyenEnvironment,
    clientKey,
    locale,
    t,
    countryCode,
    currency,
    amount,
    paymentMethodsResponse,
    onSubmit,
    onFatalError,
    buttonColor,
    buttonType,
    siteId,
    sid,
    customerId,
    customerType,
    track,
  });
  return (
    <div
      id={GOOGLE_PAY_CONTAINER_ID}
      className={classNames(styles.googlePayContainer, styles.googlePayRoundBorder)}
      data-zta={GOOGLE_PAY_BUTTON_ID}
      data-testid={GOOGLE_PAY_BUTTON_ID}></div>
  );
};

export default GooglePayButton;
